import * as Sentry from '@sentry/react'

import { useAuthStore } from '@/stores/auth'

import type { LogContext, LoggerProvider, LogLevel } from '../types'

export class SentryProvider implements LoggerProvider {
  name = 'sentry'

  private enrichScope(): void {
    const authStore = useAuthStore.getState()
    const isAuthenticated = authStore.isAuthenticated()

    if (isAuthenticated) {
      Sentry.setUser({
        id: authStore.accessToken ? 'authenticated' : undefined,
      })
    } else {
      Sentry.setUser(null)
    }
  }

  private addContextToSentry(context?: LogContext): void {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
          Sentry.setContext(key, value as Record<string, unknown>)
        } else {
          Sentry.setContext(key, { value })
        }
      })
    }
  }

  private formatError(error: Error): Record<string, unknown> {
    const formatted: Record<string, unknown> = {
      name: error.name,
      message: error.message,
      stack: error.stack,
    }

    if ('cause' in error && error.cause) {
      formatted.cause = String(error.cause)
    }

    return formatted
  }

  log(
    level: LogLevel,
    message: string,
    context?: LogContext,
    error?: Error,
    category?: string,
  ): void {
    this.enrichScope()

    const enrichedContext: LogContext = {
      ...context,
      timestamp: new Date().toISOString(),
      environment: import.meta.env.MODE,
      ...(category && { category }),
      ...(error && { error: this.formatError(error) }),
    }

    this.addContextToSentry(enrichedContext)

    if (level === 'error' || level === 'fatal') {
      if (error) {
        Sentry.captureException(error, {
          level: level === 'fatal' ? 'fatal' : 'error',
          contexts: { custom: enrichedContext },
          tags: {
            ...(category && { category }),
            logLevel: level,
          },
          fingerprint: [
            level,
            error.name,
            message,
            ...(category ? [category] : []),
          ],
        })
      } else {
        Sentry.captureMessage(message, {
          level: level === 'fatal' ? 'fatal' : 'error',
          contexts: { custom: enrichedContext },
          tags: {
            ...(category && { category }),
            logLevel: level,
          },
        })
      }
    } else {
      Sentry.addBreadcrumb({
        message,
        level:
          level === 'warning' ? 'warning' : level === 'info' ? 'info' : 'debug',
        category: category || 'log',
        data: enrichedContext,
        timestamp: Date.now() / 1000,
      })
    }
  }

  setUser(
    userId: string,
    email: string,
    name: string,
    userAccountId: string,
  ): void {
    Sentry.setUser({
      id: userId,
      email,
      username: name,
      name,
      userAccountId,
    })
  }

  clearUser(): void {
    Sentry.setUser(null)
  }

  setTag(key: string, value: string | number | boolean): void {
    Sentry.setTag(key, value)
  }

  setTags(tags: Record<string, string | number | boolean>): void {
    Object.entries(tags).forEach(([key, value]) => {
      Sentry.setTag(key, value)
    })
  }

  setContext(key: string, context: Record<string, unknown> | null): void {
    Sentry.setContext(key, context)
  }

  addBreadcrumb(
    message: string,
    category?: string,
    level?: string,
    data?: Record<string, unknown>,
  ): void {
    Sentry.addBreadcrumb({
      message,
      category: category || 'manual',
      level: level as Sentry.SeverityLevel,
      data,
      timestamp: Date.now() / 1000,
    })
  }

  captureEvent(
    message: string,
    level: string = 'info',
    extras?: Record<string, unknown>,
  ): void {
    Sentry.captureEvent({
      message,
      level: level as Sentry.SeverityLevel,
      extra: extras,
    })
  }

  startSpan<T>(name: string, op: string, callback: () => T): T {
    return Sentry.startSpan({ name, op }, callback)
  }
}
